import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import useUIStore from '../../store/uiStore';
import useDocumentStore from '../../store/documentStore';
import useAuthStore from '../../store/authStore';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import MobileDrawer from './MobileDrawer';
import TopBar from './TopBar';
import {
  MagnifyingGlassIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    leftSidebarCollapsed,
    rightSidebarCollapsed,
    mobileMenuOpen,
    closeMobileMenu,
    toggleLeftSidebar,
    theme,
  } = useUIStore();
  const { currentDocument } = useDocumentStore();
  const { user } = useAuthStore();
  const prevPathRef = useRef(location.pathname);
  const [searchQuery, setSearchQuery] = useState('');

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/documents?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  // Apply theme class to HTML element
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  // Close mobile menu on route change (only when path actually changes)
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      closeMobileMenu();
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, closeMobileMenu]);

  // Determine if we should show right sidebar based on route
  const showRightSidebar = location.pathname.includes('/pages/');

  // Hide the left sidebar on detail/working pages where the page itself has
  // dense UI (page thumbnails, document preview, fields panel, etc.) and the
  // outer sidebar competes for visual space. Users navigate back via the
  // existing back-arrow on the page header. Top-level list pages
  // (Dashboard, /documents, /idp, /tools, admin) keep the sidebar.
  const pathname = location.pathname;
  const hideLeftSidebar = (
    /^\/documents\/[^/]+/.test(pathname) ||     // /documents/:id, /documents/:id/crop
    /^\/pages\/[^/]+\/edit/.test(pathname) ||   // /pages/:id/edit
    /^\/idp\/[^/]+/.test(pathname) ||           // /idp/:id (not /idp list)
    /^\/tools\/[^/]+/.test(pathname) ||         // individual tool pages, not /tools catalog
    /^\/admin\/roles\//.test(pathname) ||       // /admin/roles/new, /admin/roles/:id
    /^\/admin\/settings\//.test(pathname) ||    // /admin/settings/* (dedicated settings layout)
    /^\/admin\/tool-settings/.test(pathname) || // /admin/tool-settings (tool enable/disable page)
    /^\/user\/settings\//.test(pathname)        // /user/settings/*
  );
  const showLeftSidebar = !hideLeftSidebar;

  // Tool pages don't have their own back button (unlike document detail / IDP
  // detail). Show a small inline "Back to tools" link at the top of these
  // pages so users aren't stranded with no nav.
  const isToolDetailPage = /^\/tools\/[^/]+/.test(pathname);
  const isToolSettingsPage = /^\/admin\/tool-settings/.test(pathname);

  return (
    <div className="h-screen flex flex-col bg-theme-primary overflow-hidden transition-colors duration-300">
      {/* Desktop Header - Full Width (hidden on detail/working pages — same scope as left sidebar) */}
      {showLeftSidebar && (
        <header className="hidden lg:flex items-center h-[68px] px-4 bg-theme-secondary border-b border-theme-primary transition-colors duration-300 z-30">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 mr-6">
            <img
              src="/images/lensverse_logo.png"
              alt="LensVerse"
              className="h-8 w-8"
            />
            <span className="text-lg font-bold text-theme-primary">LensVerse</span>
          </Link>


          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex-1 max-w-xl">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-muted" />
              <input
                type="text"
                placeholder="Search documents, IDs, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-theme-tertiary border border-theme-primary rounded-lg text-sm text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all"
              />
            </div>
          </form>

          {/* Right Section – User + Settings */}
          <div className="flex items-center gap-3 ml-auto">
            {/* User Avatar with Hover Popup */}
            <div className="relative group">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-orange to-brand-amber flex items-center justify-center text-white font-semibold text-sm shadow-md cursor-pointer">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>

              {/* Hover Popup */}
              <div className="absolute right-0 top-full mt-2 w-64 bg-theme-secondary border border-theme-primary rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                {/* Arrow */}
                <div className="absolute -top-2 right-3 w-4 h-4 bg-theme-secondary border-l border-t border-theme-primary rotate-45" />

                <div className="relative p-4">
                  {/* User Info */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-orange to-brand-amber flex items-center justify-center text-white font-bold text-base shadow-md flex-shrink-0">
                      {user?.username?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-theme-primary truncate">
                        {user?.first_name && user?.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user?.username || 'User'}
                      </p>
                      <p className="text-xs text-theme-muted truncate">{user?.email || ''}</p>
                    </div>
                  </div>

                  {/* Role Badge */}
                  <div className="pt-3 border-t border-theme-primary">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-theme-muted uppercase tracking-wider">Role</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        user?.is_superuser
                          ? 'bg-red-500/15 text-red-400'
                          : user?.profile?.account_type === 'enterprise_admin'
                            ? 'bg-violet-500/15 text-violet-400'
                            : user?.profile?.account_type === 'enterprise_user'
                              ? 'bg-blue-500/15 text-blue-400'
                              : 'bg-emerald-500/15 text-emerald-400'
                      }`}>
                        {user?.is_superuser
                          ? 'Superuser'
                          : user?.profile?.account_type === 'enterprise_admin'
                            ? 'Enterprise Admin'
                            : user?.profile?.account_type === 'enterprise_user'
                              ? 'Enterprise User'
                              : 'Individual User'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Settings Gear Icon */}
            <button
              onClick={() => {
                const isAdmin = user?.is_superuser;
                navigate(isAdmin ? '/admin/settings/users' : '/user/settings/profile');
              }}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-theme-muted hover:text-brand-orange hover:bg-theme-tertiary transition-all duration-200"
              title="Settings"
            >
              <Cog6ToothIcon className="w-5 h-5" />
            </button>
          </div>
        </header>
      )}

      {/* Mobile Top Bar */}
      <div className="lg:hidden">
        <TopBar />
      </div>

      {/* Content Area - Sidebar + Main */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Desktop (hidden on detail/working pages) */}
        {showLeftSidebar && (
          <div className="hidden lg:block relative">
            <LeftSidebar collapsed={leftSidebarCollapsed} />
            {/* Floating Collapse Button */}
            <button
              onClick={toggleLeftSidebar}
              className="absolute top-4 -right-3 z-50 w-6 h-6 bg-theme-secondary border border-theme-primary rounded-full shadow-md flex items-center justify-center hover:bg-theme-tertiary hover:border-brand-orange/50 text-theme-muted hover:text-brand-orange transition-all duration-200"
              title={leftSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {leftSidebarCollapsed ? (
                <ChevronRightIcon className="w-4 h-4" />
              ) : (
                <ChevronLeftIcon className="w-4 h-4" />
              )}
            </button>
          </div>
        )}

        {/* Main Content Area */}
        <main className="flex-1 flex flex-col min-w-0">
          {/* Page Content */}
          <div className="flex-1 overflow-auto">
            <div className="min-h-full p-4 lg:p-5">
              {/* Inline "Back to tools" — only shown on /tools/:tool pages.
                  Other detail pages have their own back arrows. */}
              {isToolDetailPage && (
                <button
                  type="button"
                  onClick={() => navigate('/tools')}
                  className="hidden lg:inline-flex items-center gap-1.5 mb-4 text-sm text-theme-muted hover:text-brand-orange transition-colors"
                  title="Back to Tool Catalog"
                  aria-label="Back to tools"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  <span>Back to tools</span>
                </button>
              )}
              {isToolSettingsPage && (
                <button
                  type="button"
                  onClick={() => navigate('/admin/settings/system')}
                  className="hidden lg:inline-flex items-center gap-1.5 mb-4 text-sm text-theme-muted hover:text-brand-orange transition-colors"
                  title="Back to System Settings"
                  aria-label="Back to system settings"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  <span>Back to System Settings</span>
                </button>
              )}
              <Outlet />
            </div>
          </div>
        </main>

        {/* Right Sidebar - Desktop (contextual) */}
        {showRightSidebar && (
          <div className="hidden xl:block">
            <RightSidebar
              collapsed={rightSidebarCollapsed}
              document={currentDocument}
            />
          </div>
        )}
      </div>

      {/* Mobile Drawer */}
      <MobileDrawer open={mobileMenuOpen} onClose={closeMobileMenu} />

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}

// Mobile Bottom Navigation Component
function MobileBottomNav() {
  const location = useLocation();
  const { openMobileMenu } = useUIStore();

  const navItems = [
    { name: 'Home', href: '/dashboard', icon: 'home' },
    { name: 'Upload', href: '/upload', icon: 'upload' },
    { name: 'Docs', href: '/documents', icon: 'docs' },
    { name: 'More', icon: 'more', isMore: true },
  ];

  const isActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const getIcon = (icon, active) => {
    const baseClass = `w-6 h-6 ${active ? 'text-brand-orange' : 'text-theme-muted'}`;

    switch (icon) {
      case 'home':
        return (
          <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 2.5 : 2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        );
      case 'upload':
        return (
          <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 2.5 : 2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        );
      case 'docs':
        return (
          <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 2.5 : 2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'more':
        return (
          <svg className={baseClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-theme-secondary border-t border-theme-primary safe-area-bottom z-40 transition-colors duration-300">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          if (item.isMore) {
            return (
              <button
                key={item.name}
                onClick={openMobileMenu}
                className="flex flex-col items-center justify-center w-16 py-1 transition-colors"
              >
                {getIcon(item.icon, false)}
                <span className="text-xs font-medium mt-0.5 text-theme-muted">{item.name}</span>
              </button>
            );
          }

          const active = isActive(item.href);
          return (
            <Link
              key={item.name}
              to={item.href}
              className="flex flex-col items-center justify-center w-16 py-1 transition-colors"
            >
              {getIcon(item.icon, active)}
              <span className={`text-xs font-medium mt-0.5 ${active ? 'text-brand-orange' : 'text-theme-muted'}`}>
                {item.name}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
